import { useState, useRef, useEffect } from 'react';
import { Send, ArrowLeft, Brain, Sparkles, Download, Copy, Check } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { ResultsGraph } from '../components/ResultsGraph';
import { HarmonicGraph } from '../components/HarmonicGraph';
import { useAssessment } from '../contexts/AssessmentContext';
import { getAIAnalysis } from '../services/analysis';
import { useResults } from '../contexts/ResultsContext';
import { validateAIAnalysis } from '../utils/aiValidation';
import { auth } from '../lib/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { checkCoachAIAccess } from '../lib/auth';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface Message {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
}

const analysisButtons = [
  { id: 'intro', label: 'Intro to Report', query: 'Summarize my assessment results, highlighting key insights and themes. Provide a clear overview of my traits, Harmonic Scale position, and areas for personal and professional growth.' },
  { id: 'traits', label: 'Trait Insights', query: 'Analyze all 12 traits in the Being, Doing, and Having categories. Provide insights into each trait, highlighting my scores, how they reflect my current behaviors, and their impact on my daily actions. Identify the most notable traits in each category (high-performing or requiring attention) and explain their role in shaping my personal and professional dynamics.' },
  { id: 'harmonic', label: 'Harmonic Scale', query: 'Explain my position on the Harmonic Scale. How does my emotional expression influence my traits and behaviors? Provide examples of how this interaction impacts my decision-making, relationships, and daily actions' },
  { id: 'strengths', label: 'Key Strengths', query: 'Identify my strongest traits and explain how I can use them to overcome challenges and achieve success. Offer examples of how these strengths can be applied in both personal and professional settings.' },
  { id: 'breakthrough', label: 'Breakthrough Insights', query: 'Break down my Critical, Development, and Growth Zones. Highlight which traits need immediate focus, which ones have potential for steady improvement, and which traits can be leveraged for maximum success.' },
  { id: 'professional', label: 'Professional Insights', query: 'Analyze how my traits manifest in professional contexts, including leadership, teamwork, communication, adaptability, and problem-solving. Provide actionable strategies to enhance my effectiveness in the workplace.' },
  { id: 'relationships', label: 'Relationship Insights', query: 'Provide insights into how my traits and Harmonic Scale position influence my relationships. Offer strategies to improve communication, build trust, and strengthen personal and professional connections.' },
  { id: 'recommendations', label: 'Action Steps', query: 'Based on my assessment, provide clear and specific action steps I can take to improve my traits, elevate my Harmonic Scale position, and achieve my personal and professional goals. Include both short-term and long-term recommendations.' },
];

export function AIAnalysisView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { scores, harmonicScores, setScores, setHarmonicScores, formatDataForAI } = useAssessment();
  const { getResults } = useResults();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const analysisContentRef = useRef<HTMLDivElement>(null);
  const [coachAffiliation, setCoachAffiliation] = useState<{
    link: string;
    buttonText: string;
  } | null>(null);

  // Determine the correct back path based on context
  const targetUserId = searchParams.get('userId');
  const isCoachViewing = targetUserId && targetUserId !== auth.currentUser?.uid;
  const backPath = isCoachViewing ? `/coach/results/${targetUserId}` : '/results';

  useEffect(() => {
    const loadResults = async () => {
      if (!auth.currentUser) {
        navigate('/login');
        return;
      }

      try {
        // Clear previous state when switching respondents
        setMessages([]);
        setScores([]);
        setHarmonicScores([]);
        setError(null);
        
        const userId = targetUserId || auth.currentUser.uid;
        
        // If accessing as coach, verify permissions
        if (userId !== auth.currentUser.uid) {
          const hasAccess = await checkCoachAIAccess(auth.currentUser.uid);
          if (!hasAccess) {
            setError('You do not have access to AI analysis');
            setIsInitializing(false);
            return;
          }
        }

        // Always fetch results for the current user to ensure fresh data
        const results = await getResults(userId, auth.currentUser.uid);
        if (results) {
          setScores(results.scores || []);
          setHarmonicScores(results.harmonicScores || []);
          
          // Load coach affiliation data if there's a coachId
          if (results.coachId) {
            await loadCoachAffiliation(results.coachId);
          }
        } else {
          navigate('/results');
        }
      } catch (error) {
        console.error('Error loading results:', error);
        setError('Failed to load assessment results');
      } finally {
        setIsInitializing(false);
      }
    };

    loadResults();
  }, [targetUserId, getResults, setScores, setHarmonicScores, navigate]);

  const loadCoachAffiliation = async (coachId: string) => {
    try {
      const coachQuery = query(
        collection(db, 'coaches'),
        where('userId', '==', coachId)
      );
      const coachSnapshot = await getDocs(coachQuery);
      
      if (!coachSnapshot.empty) {
        const coachData = coachSnapshot.docs[0].data();
        if (coachData.affiliationLink && coachData.affiliationButtonText) {
          setCoachAffiliation({
            link: coachData.affiliationLink,
            buttonText: coachData.affiliationButtonText
          });
        }
      }
    } catch (error) {
      console.error('Error loading coach affiliation:', error);
    }
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (content: string) => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      
      const userMessage: Message = {
        id: Date.now().toString(),
        content,
        sender: 'user',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, userMessage]);
      setInputMessage('');

      const formattedPrompt = formatDataForAI(content);
      const response = await getAIAnalysis(formattedPrompt);

      let displayContent = response;
      if (scores && scores.length > 0) {
        const validation = validateAIAnalysis(response, scores);
        if (!validation.valid) {
          displayContent = `${response}\n\n---\n\n**⚠️ Validation Notice:** The following issues were detected in this AI analysis:\n\n${validation.errors.map(e => `- ${e}`).join('\n')}\n\nPlease contact your coach if you notice discrepancies between this analysis and your graph.`;
        }
      }

      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: displayContent,
        sender: 'ai',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        sender: 'ai',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputMessage.trim() && !isLoading) {
      sendMessage(inputMessage);
    }
  };

  const handleButtonClick = (query: string) => {
    if (!isLoading) {
      sendMessage(query);
    }
  };

  const showSuccessToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleExportPDF = async () => {
    if (!analysisContentRef.current || messages.length === 0) {
      showSuccessToast('No analysis data to export');
      return;
    }

    try {
      // Dynamically import html2pdf to avoid build issues
      const html2pdf = (await import('html2pdf.js')).default;
      
      // Create a clean version of the content for PDF
      const element = document.createElement('div');
      element.style.cssText = `
        padding: 20px;
        font-family: Arial, sans-serif;
        line-height: 1.6;
        color: #333;
        background-color: #fff;
        max-width: 100%;
        word-wrap: break-word;
        overflow-wrap: break-word;
      `;

      // Add header
      const header = document.createElement('div');
      header.innerHTML = `
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #4F46E5; padding-bottom: 15px; page-break-inside: avoid;">
          <h1 style="color: #4F46E5; margin: 0; font-size: 24px; font-weight: bold;">AI Analysis Report</h1>
          <p style="margin: 8px 0 0 0; color: #666; font-size: 14px;">Generated on ${new Date().toLocaleDateString()}</p>
        </div>
      `;
      element.appendChild(header);

      // Add scores summary
      if (scores && harmonicScores) {
        const scoresSection = document.createElement('div');
        scoresSection.innerHTML = `
          <div style="margin-bottom: 25px; page-break-inside: avoid;">
            <h2 style="color: #4F46E5; border-bottom: 1px solid #E5E7EB; padding-bottom: 8px; font-size: 18px; margin-bottom: 15px;">Assessment Summary</h2>
            <div style="display: flex; flex-wrap: wrap; gap: 15px; margin-top: 15px;">
              <div style="flex: 1; min-width: 250px;">
                <h3 style="color: #374151; margin-bottom: 12px; font-size: 14px; font-weight: bold;">Top Characteristics</h3>
                ${scores.sort((a, b) => b.normalized - a.normalized).slice(0, 5).map(score => 
                  `<div style="margin-bottom: 6px; font-size: 12px; word-wrap: break-word;"><strong>${score.characteristicId}:</strong> ${Math.round(score.normalized)}%</div>`
                ).join('')}
              </div>
              <div style="flex: 1; min-width: 250px;">
                <h3 style="color: #374151; margin-bottom: 12px; font-size: 14px; font-weight: bold;">Top Harmonic Levels</h3>
                ${harmonicScores.sort((a, b) => b.normalized - a.normalized).slice(0, 5).map(score => 
                  `<div style="margin-bottom: 6px; font-size: 12px;"><strong>Level ${score.levelId}:</strong> ${Math.round(score.normalized)}%</div>`
                ).join('')}
              </div>
            </div>
          </div>
        `;
        element.appendChild(scoresSection);
      }

      // Add chat messages
      const chatSection = document.createElement('div');
      chatSection.innerHTML = `<h2 style="color: #4F46E5; border-bottom: 1px solid #E5E7EB; padding-bottom: 8px; margin-bottom: 15px; font-size: 18px; page-break-before: auto;">Analysis Conversation</h2>`;
      
      messages.forEach((message, index) => {
        const messageDiv = document.createElement('div');
        messageDiv.style.cssText = `
          margin-bottom: 20px;
          padding: 12px;
          border-left: ${message.sender === 'user' ? '4px solid #3B82F6' : '4px solid #10B981'};
          background-color: ${message.sender === 'user' ? '#EFF6FF' : '#F0FDF4'};
          page-break-inside: avoid;
          word-wrap: break-word;
          overflow-wrap: break-word;
          hyphens: auto;
        `;
        
        messageDiv.innerHTML = `
          <div style="font-weight: bold; color: ${message.sender === 'user' ? '#1E40AF' : '#059669'}; margin-bottom: 6px; font-size: 13px;">
            ${message.sender === 'user' ? 'Question' : 'AI Analysis'}
          </div>
          <div style="white-space: pre-wrap; word-wrap: break-word; overflow-wrap: break-word; font-size: 12px; line-height: 1.5;">${message.content}</div>
        `;
        chatSection.appendChild(messageDiv);
      });
      
      element.appendChild(chatSection);

      const opt = {
        margin: [0.4, 0.4, 0.4, 0.4],
        filename: `ai-analysis-report-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 1.5, 
          useCORS: true,
          allowTaint: true,
          letterRendering: true,
          logging: false
        },
        jsPDF: { 
          unit: 'in', 
          format: 'letter', 
          orientation: 'portrait',
          compress: true
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().from(element).set(opt).save();
      showSuccessToast('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showSuccessToast('Failed to export PDF');
    }
  };

  const handleCopyAIResponse = async () => {
    const aiMessages = messages.filter(msg => msg.sender === 'ai');
    if (aiMessages.length === 0) {
      showSuccessToast('No AI responses to copy');
      return;
    }

    try {
      // Get the last AI response or all AI responses
      const lastAIResponse = aiMessages[aiMessages.length - 1].content;
      await navigator.clipboard.writeText(lastAIResponse);
      showSuccessToast('AI response copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      showSuccessToast('Failed to copy to clipboard');
    }
  };

  const handleAffiliationClick = () => {
    if (coachAffiliation?.link) {
      const url = coachAffiliation.link.startsWith('http') 
        ? coachAffiliation.link 
        : `https://${coachAffiliation.link}`;
      window.open(url, '_blank');
    }
  };

  if (isInitializing) {
    return <LoadingSpinner message="Loading results..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen minimal-bg py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-1">{error}</h1>
            <p className="text-gray-600 mb-4">Please contact your coach for assistance</p>
            <Link
              to="/results"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium hover:scale-105 transition-all"
            >
              Back to Results
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!scores || !harmonicScores) {
    return (
      <div className="min-h-screen minimal-bg py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-1">No Assessment Results Found</h1>
            <p className="text-gray-600">Please complete the assessment first to get AI analysis</p>
          </div>
          <Link
            to="/assessment"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium hover:scale-105 transition-all"
          >
            Take Assessment
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <Link 
          to={backPath}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-4 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Results
        </Link>

        {/* Modified Header with Logo */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative">
            <Brain className="w-12 h-12 text-blue-600" />
            <Sparkles className="w-4 h-4 text-yellow-500 absolute -top-1 -right-1" />
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-1">AI Analysis Report</h1>
            <p className="text-gray-600">Get detailed insights about your assessment results</p>
          </div>
        </div>

        {/* Results Graphs - Side by Side */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 shadow-sm border border-green-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              Characteristic Profile
            </h2>
            <ResultsGraph scores={scores} />
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-8 shadow-sm border border-purple-200">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              Harmonic Scale Profile
            </h2>
            <HarmonicGraph scores={harmonicScores} />
          </div>
        </div>

        {/* Chat Interface */}
        <div ref={analysisContentRef} className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 mb-6 shadow-sm border border-indigo-200">
          <h2 className="text-lg font-bold text-gray-900 mb-4">AI Analysis</h2>
          <div className="h-[500px] overflow-y-auto custom-scrollbar mb-4 bg-white rounded-xl p-4 border border-indigo-100">
            {messages.map(message => (
              <div
                key={message.id}
                className={`mb-4 ${
                  message.sender === 'user' ? 'text-right' : 'text-left'
                }`}
              >
                <div
                  key={message.id}
                  className={`inline-block max-w-[80%] px-8 py-4 rounded-2xl ${
                    message.sender === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-900 border border-gray-200'
                  }`}
                >
                  {message.sender === 'user' ? (
                    message.content
                  ) : (
                    <ReactMarkdown
                      components={{
                        h1: ({ children }) => <h1 className="text-xl font-bold mb-3">{children}</h1>,
                        h2: ({ children }) => <h2 className="text-lg font-bold mb-2">{children}</h2>,
                        h3: ({ children }) => <h3 className="text-base font-bold mb-2">{children}</h3>,
                        p: ({ children }) => <p className="mb-4 last:mb-0">{children}</p>,
                        strong: ({ children }) => <strong className="font-bold text-gray-900">{children}</strong>,
                        ul: ({ children }) => <ul className="list-disc ml-6 mb-4">{children}</ul>,
                        ol: ({ children }) => <ol className="list-decimal ml-6 mb-4">{children}</ol>,
                        li: ({ children }) => <li className="mb-1">{children}</li>,
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="text-gray-500 font-medium">Human Insights AI is analyzing...</div>
            )}
            <div ref={chatEndRef} />
          </div>

          <form onSubmit={handleSubmit} className="flex gap-4">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Ask about your results..."
              className="flex-1 px-6 py-3 rounded-xl bg-white text-gray-900 border border-gray-300 
                focus:border-blue-500 focus:outline-none"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={isLoading || !inputMessage.trim()}
              className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 
                text-white font-medium hover:scale-105 transition-all disabled:opacity-50"
            >
              <Send className="w-5 h-5" />
            </button>
          </form>
        </div>

        {/* Analysis Buttons */}
        {coachAffiliation && (
          <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm border border-gray-200">
            <div className="flex justify-center">
              <button
                onClick={handleAffiliationClick}
                className="px-8 py-4 rounded-xl bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 
                  text-white font-medium hover:scale-105 transition-all text-lg"
              >
                {coachAffiliation.buttonText}
              </button>
            </div>
          </div>
        )}
        
        <div className="bg-gradient-to-br from-emerald-50 to-green-100 rounded-2xl p-6 shadow-sm border border-emerald-200">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Analysis</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {analysisButtons.map(button => (
              <button
                key={button.id}
                onClick={() => handleButtonClick(button.query)}
                disabled={isLoading}
                className="px-4 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-medium
                  transition-all disabled:opacity-50 text-sm shadow-md hover:shadow-lg hover:scale-105"
              >
                {button.label}
              </button>
            ))}
          </div>
        </div>

        {/* Export Options */}
        <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Export Options</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={handleExportPDF}
              disabled={messages.length === 0}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 
                text-white font-medium hover:scale-105 transition-all disabled:opacity-50"
            >
              <Download className="w-5 h-5" />
              Export as PDF
            </button>
            
            <button
              onClick={handleCopyAIResponse}
              disabled={messages.filter(msg => msg.sender === 'ai').length === 0}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 
                text-white font-medium hover:scale-105 transition-all disabled:opacity-50"
            >
              <Copy className="w-5 h-5" />
              Copy AI Response
            </button>
          </div>
        </div>
      </div>
      
      {/* Toast Notification */}
      {showToast && (
        <div className="fixed bottom-4 right-4 z-50">
          <div className="flex items-center gap-2 bg-emerald-500 text-white px-6 py-3 rounded-xl shadow-lg">
            <Check className="w-5 h-5" />
            <span>{toastMessage}</span>
          </div>
        </div>
      )}
    </div>
  );
}